import { computed, MaybeRefOrGetter, toRef } from 'vue';
import type {
  JobState,
  QaEquipmentStateOfUse,
  RoleStateOfUse,
  StateOfUse,
  UserState,
  UserStateOfUse,
} from '@/api/generator';
import { useTranslation } from 'i18next-vue';
import { I18nKey } from './typings';

type State =
  | StateOfUse
  | UserStateOfUse
  | UserState
  | RoleStateOfUse
  | QaEquipmentStateOfUse
  | JobState
  | 'Adding';

export interface MayHaveState {
  state?: State;
}

type EntityState = 'adding' | 'inUse' | 'editing' | 'ready' | 'other';

const stateMapping: Record<State, EntityState> = {
  // All Entities
  InAdding: 'adding',
  InEditing: 'editing',
  Enabled: 'ready',
  Disabled: 'other',
  Archived: 'other',
  Deleted: 'other',
  // User
  NoActivationSent: 'other',
  ActivationPending: 'other',
  Activated: 'ready',
  // QaEquipment
  NotRegistered: 'other',
  DeRegistered: 'other',
  // Common
  Adding: 'adding',
  // JobOccurrence
  New: 'ready',
  InExecution: 'editing',
  Paused: 'ready',
  Completed: 'ready',
  ReviewPending: 'ready',
  InReview: 'ready',
  ReworkRequested: 'ready',
  Approved: 'ready',
};

export function useEntityState(entity: MaybeRefOrGetter<MayHaveState | undefined>) {
  const { t } = useTranslation();

  const entityRef = toRef(entity);

  const state = computed(() => entityRef.value?.state ?? 'InAdding');

  const entityState = computed(() => stateMapping[state.value]);

  const label = computed(() => t(`StateOfUseEnum.${state.value}` as I18nKey) + ' ');

  return {
    state,
    entityState,
    label,
  };
}
